/**
 * NavigationMenu.tsx - Navigation menu using Selectr for display
 * 
 * CHANGE HISTORY:
 * - 251122_1508 danielbeer: Created to replace SideMenu with Selectr integration
 * 
 * ARCHITECTURE:
 * - Navigatr: Handles navigation state, history, routing
 * - Selectr: Handles display, filtering, keyboard navigation
 * - Filtr: Handles advanced filtering (fuzzy search, multi-field)
 * 
 * This separates concerns:
 * - Navigation logic (Navigatr)
 * - Display logic (Selectr)
 * - Filter logic (Filtr)
 */

import React, { useState, useEffect, useMemo } from 'react';
import { Selectr } from '@opcentral/selectr';
import type { SelectrItem } from '@opcentral/selectr';
import type { NavigationContext, NavigationItem } from './ContextSelector';

export interface NavigationMenuProps {
  // Context
  context?: NavigationContext;
  items?: NavigationItem[];
  
  // Current state
  currentItemId?: string;
  
  // Callbacks
  onNavigate: (itemId: string, item: NavigationItem) => void;
  onFavorite?: (itemId: string) => void;
  
  // Display options
  mode?: 'dropdown' | 'list' | 'menu' | 'modal';
  searchable?: boolean;
  grouped?: boolean;
  showIcons?: boolean;
  showDescriptions?: boolean;
  
  // Filter options (passed to Filtr)
  filterConfig?: {
    fuzzySearch?: boolean;
    searchFields?: string[];
    highlightMatches?: boolean;
    caseSensitive?: boolean;
    minScore?: number;
  };
  
  // Layout options
  width?: string | number;
  maxHeight?: number;
}

/**
 * NavigationMenu Component
 * 
 * Uses Selectr for display with Filtr for advanced filtering
 * Navigatr handles the navigation logic, this just displays the menu
 */
export const NavigationMenu: React.FC<NavigationMenuProps> = ({
  context,
  items: propItems,
  currentItemId,
  onNavigate,
  onFavorite,
  mode = 'list',
  searchable = true,
  grouped = true,
  showIcons = true,
  showDescriptions = true,
  filterConfig = {
    fuzzySearch: true,
    searchFields: ['label', 'description', 'tags'],
    highlightMatches: true,
    caseSensitive: false,
    minScore: 0.3
  },
  width = '100%',
  maxHeight = 600
}) => {
  // Get items from context or props
  const navigationItems = useMemo(() => {
    return context?.items || propItems || [];
  }, [context, propItems]);

  // Convert NavigationItems to SelectrItems
  const selectrItems: SelectrItem[] = useMemo(() => {
    return navigationItems.map(item => ({
      id: item.id,
      label: item.label,
      value: item,
      icon: showIcons ? item.icon : undefined,
      description: showDescriptions ? item.description : undefined,
      group: grouped ? item.category : undefined,
      metadata: {
        tags: item.tags,
        isCurrent: item.id === currentItemId,
        ...item.metadata
      }
    }));
  }, [navigationItems, currentItemId, showIcons, showDescriptions, grouped]);

  // Handle selection
  const handleSelect = (selected: SelectrItem | SelectrItem[]) => {
    if (Array.isArray(selected)) return; // Single select only for navigation
    
    const item = selected.value as NavigationItem;
    
    // Execute item action if it exists
    if (item.action) {
      item.action();
    }
    
    // Notify parent
    onNavigate(item.id, item);
  };

  // Custom search function with Filtr integration
  const handleSearch = (query: string) => {
    console.log('[NavigationMenu] Search query:', query);
    // Selectr will handle the filtering using its built-in logic
    // In the future, we can integrate Filtr here for advanced filtering
  };

  return (
    <div className="navigation-menu">
      <Selectr
        source="array"
        items={selectrItems}
        mode="single"
        uiMode={mode}
        searchable={searchable}
        grouped={grouped}
        placeholder="Search navigation..."
        emptyMessage="No items found"
        onSelect={handleSelect}
        onSearch={handleSearch}
        closeOnSelect={mode === 'dropdown'}
        width={width}
        maxHeight={maxHeight}
        // Pass current selection
        value={selectrItems.find(item => item.id === currentItemId)}
      />

      <style>{`
        .navigation-menu {
          height: 100%;
          display: flex;
          flex-direction: column;
        }

        /* Highlight current item */
        .selectr-item[data-current="true"] {
          background: var(--accent-primary, #3b82f6);
          color: white;
          font-weight: 600;
        }

        /* Favorite star button */
        .selectr-item-actions {
          display: flex;
          gap: 0.5rem;
          margin-left: auto;
        }

        .btn-favorite {
          background: none;
          border: none;
          cursor: pointer;
          font-size: 1rem;
          opacity: 0.5;
          transition: opacity 0.2s;
        }

        .btn-favorite:hover {
          opacity: 1;
        }

        .btn-favorite.active {
          opacity: 1;
          color: #fbbf24;
        }
      `}</style>
    </div>
  );
};

export default NavigationMenu;
